import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { markUpdateApplied, useForceRefresh } from '../hooks/useForceRefresh';

// Check for updates every 60 seconds when page is visible
const UPDATE_CHECK_INTERVAL = 60 * 1000;

/**
 * Колко чакаме новият service worker да поеме страницата, преди да минем по
 * твърдия път (`useForceRefresh`).
 *
 * 🔴 Разрешеното обещание на `updateServiceWorker` НЕ е сигнал за успех. В
 * vite-plugin-pwa 1.2.0 то се разрешава веднага след ИЗПРАЩАНЕТО на
 * SKIP_WAITING (`register.js` → workbox-window `messageSkipWaiting` →
 * `void messageSW`), тоест преди новият SW изобщо да е активиран. До TASK_42
 * точно то се броеше за успех: страницата се презареждаше под СТАРИЯ SW (същият
 * bundle), а зеленото „обновено" съобщаваше за промяна, която не е станала.
 * Успехът е `controllerchange` — новият SW е поел страницата.
 */
export const SW_TAKEOVER_TIMEOUT_MS = 3000;

export function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTriggered = useRef(false);
  const { forceRefresh } = useForceRefresh();

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      if (import.meta.env.DEV) console.log('SW Registered:', registration);

      // Periodic update check - important for iOS
      if (registration) {
        // Check immediately
        registration.update().catch(console.error);

        // Then check periodically
        setInterval(() => {
          if (document.visibilityState === 'visible') {
            if (import.meta.env.DEV) console.log('PWA: Checking for updates...');
            registration.update().catch(console.error);
          }
        }, UPDATE_CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error', error);
    },
  });

  // Also check for updates when page becomes visible (important for iOS)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.serviceWorker?.controller) {
        if (import.meta.env.DEV) console.log('PWA: Page visible, checking for updates...');
        navigator.serviceWorker.ready.then(registration => {
          registration.update().catch(console.error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const close = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const handleUpdate = useCallback(() => {
    // Prevent double-firing
    if (updateTriggered.current || isUpdating) {
      if (import.meta.env.DEV) console.log('PWA Update: Already triggered, ignoring');
      return;
    }

    updateTriggered.current = true;
    setIsUpdating(true);
    if (import.meta.env.DEV) console.log('PWA Update: Starting update...');

    const container = navigator.serviceWorker;
    // Решава се веднъж: първото от трите (поемане · таван · провал) печели.
    let settled = false;
    // Твърдият път е стигнал края си: записал е следата и е поискал презареждане.
    let forceRefreshDone = false;

    // Новият SW не пое навреме, или изпращането се провали → твърдият път:
    // дерегистриране + кешове + презареждане. Той сам записва честната следа.
    const fallBackToForceRefresh = () => {
      if (settled) return;
      settled = true;
      clearTimeout(takeoverTimer);
      if (import.meta.env.DEV) console.log('PWA Update: New service worker did not take over, forcing refresh');
      void forceRefresh().finally(() => {
        forceRefreshDone = true;
        container?.removeEventListener('controllerchange', onControllerChange);
      });
    };

    const onControllerChange = () => {
      if (forceRefreshDone) return;
      if (import.meta.env.DEV) console.log('PWA Update: New service worker took over');
      // Следата преживява презареждането — иначе потребителят натиска
      // „Обнови", екранът мига и нищо не му казва дали е станало.
      // Поемането Е успехът, затова се записва и когато дойде КЪСНО, докато
      // твърдият път още тече: библиотеката презарежда на `controlling` и може
      // да прекъсне твърдия път преди неговата следа. Стигне ли той края си,
      // презаписва тази със своята.
      markUpdateApplied(true);
      if (settled) return;
      settled = true;
      clearTimeout(takeoverTimer);
      // Библиотеката презарежда и сама на `controlling`; двете в една задача
      // са една навигация.
      window.location.reload();
    };

    const takeoverTimer = setTimeout(fallBackToForceRefresh, SW_TAKEOVER_TIMEOUT_MS);
    // Слушателят е ПРЕДИ изпращането — смяната може да дойде веднага.
    container?.addEventListener('controllerchange', onControllerChange, { once: true });

    updateServiceWorker(true).catch((error) => {
      console.error('PWA Update: Error:', error);
      fallBackToForceRefresh();
    });
  }, [forceRefresh, isUpdating, updateServiceWorker]);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  if (!needRefresh) return null;

  return (
    <div
      className="fixed left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[9999]"
      style={{
        bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
      }}
    >
      <div className="bg-blue-600 text-white rounded-lg shadow-lg p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <svg
            className="w-6 h-6 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="text-sm font-medium">
            {t('common.pwaUpdate.available')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className="bg-white text-blue-600 px-4 py-2 rounded-md text-sm font-semibold active:bg-blue-100 transition-colors disabled:opacity-70 disabled:cursor-wait min-w-[100px]"
          >
            {isUpdating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>...</span>
              </span>
            ) : (
              t('common.pwaUpdate.updateButton')
            )}
          </button>
          {!isUpdating && (
            <button
              type="button"
              onClick={handleClose}
              className="text-blue-200 hover:text-white p-2"
              aria-label={t('common.pwaUpdate.closeLabel')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
